import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl, FormattedMessage } from 'react-intl';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import * as api from 'apis/restaurant-api/merchant-admin/venue/api';
import PageHeader from 'common/components/PageHeader';
import { useExternalAccessRights, useRetailContext } from 'common/hooks';
import { usePermissions } from 'common/wolt-permissions';
import {
  hasMerchantAppCreateDeletePermissions,
  hasMerchantAppLoginPermissions,
} from 'common/wolt-permissions/permissionUtils';
import { useVenueContext } from 'modules/venue/VenueContext';

import AccessManagementNavigation from './AccessManagementNavigation';
import AppPasswords from './AppPasswords';
import DirectAddModal from './DirectAddModal';
import InviteModal from './InviteModal';
import UserManagement from './UserManagement';
import { reportAddExistingUser, reportInviteUser, reportRemoveAccess } from './behavioral';
import { AccessDescription } from './types';
import { parseApiError } from './utils';

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const VenueUserManagement = () => {
  const { url } = useRouteMatch();
  const intl = useIntl();
  const venue = useVenueContext();
  const isRetailContext = useRetailContext();

  const {
    data: roles = null,
    error,
    refetch,
  } = useQuery<AccessDescription[], AxiosError>(
    [`/v1/merchant-admin/venues/${venue.id}/access`],
    () => api.fetchVenueAccess(venue.id),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  const removeAccessMutation = useMutation(
    (user: AccessDescription) => api.deleteVenueAccess(venue.id, user.email, user.id),
    {
      onSuccess: () => {
        refetch();
      },
      onError: (error: AxiosError) => {
        toast.error(<FormattedMessage id={parseApiError(error)} />, { duration: Infinity });
      },
    },
  );

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  const permissions = usePermissions();
  const accessRights = useExternalAccessRights();

  const handleInviteSuccess = () => {
    setIsInviteModalOpen(false);
    refetch();
    reportInviteUser('venue');
  };

  const handleDirectAddSuccess = () => {
    setIsAddUserModalOpen(false);
    refetch();
    reportAddExistingUser('venue');
  };

  const handleRemoveAccess = async (user: AccessDescription) => {
    await removeAccessMutation.mutateAsync(user);
    reportRemoveAccess('venue');
  };

  return (
    <Root>
      <PageHeader title={intl.formatMessage({ id: 'navigation.access-management' })} />
      <Content>
        <AccessManagementNavigation
          items={[
            {
              href: `${url}/user-management`,
              name: intl.formatMessage({ id: 'navigation.user-management' }),
            },
            ...(hasMerchantAppLoginPermissions(permissions, accessRights, venue)
              ? [
                  {
                    href: `${url}/app-passwords`,
                    name: intl.formatMessage({ id: 'userManagement.appPasswords' }),
                  },
                ]
              : []),
          ]}
        />
        <Switch>
          <Route path={`${url}/user-management`}>
            <UserManagement
              entityId={venue.id}
              entityType="venue"
              errorReason={error ? parseApiError(error) : null}
              isRemovingAccess={removeAccessMutation.isLoading}
              isRetailContext={isRetailContext}
              onAddUser={() => setIsAddUserModalOpen(true)}
              onInviteUser={() => setIsInviteModalOpen(true)}
              onRemoveAccess={handleRemoveAccess}
              roles={roles}
            />
          </Route>
          <Route path={`${url}/app-passwords`}>
            <AppPasswords
              venueId={venue.id}
              canCreateDelete={hasMerchantAppCreateDeletePermissions(permissions, accessRights, venue)}
            />
          </Route>
          <Redirect from={url} exact to={`${url}/user-management`} />
        </Switch>
        <InviteModal
          entityId={venue.id}
          entityType={'venue'}
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          onSuccess={handleInviteSuccess}
        />
        <DirectAddModal
          entityId={venue.id}
          entityType={'venue'}
          isOpen={isAddUserModalOpen}
          onClose={() => setIsAddUserModalOpen(false)}
          onSuccess={handleDirectAddSuccess}
        />
      </Content>
    </Root>
  );
};

export default VenueUserManagement;
